import { MODULAR_PLAN_NAME } from "@intelligems/ig-types";
import { useCallback, useEffect, useMemo, useReducer, useRef } from "react";
import { useDispatch } from "react-redux";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";

import { useGetBillingPortal } from "@src/api/stripe/get-billing-portal";
import {
  orderVolumeTier,
  SMART_PRICING_VARIANTS,
} from "@src/constants/billing";
import { useWebsocket } from "@src/hooks/websocket";
import { setConfetti } from "@src/store/slices/global";
import {
  mpBillingCheckoutErrorShown,
  mpBillingModuleRemoved,
  mpBillingPageViewed,
  mpBillingPlanCancelConfirmed,
  mpBillingPlanChangeCompleted,
  mpBillingPlanDowngradeStarted,
  mpBillingShopifyApprovalReturned,
  mpBillingShopifyApprovalStarted,
} from "@src/utils/mixpanel";
import type {
  BillingCommandViewModel,
  BillingDialogConfirmationIntent,
  BillingEntityActionIntent,
  BillingSelectionIntent,
  BillingSettingsWorkflow,
  BillingWorkflowDialogViewModel,
} from "./types";
import {
  buildBillingCatalog,
  entityFor,
  subscriptionPeriodEndFor,
} from "./internal/catalog";
import type {
  BillingCommandAnalyticsEvent,
  BillingCommandFeedback,
  BillingCommandPlan,
} from "./internal/commandTypes";
import { planBillingCommand } from "./internal/commandPlanner";
import {
  APPROVAL_RETURN_FEEDBACK,
  APPROVAL_RETURN_POLL_CAP_MS,
  readApprovalReturnArrival,
  readApprovalReturnOutcome,
  readChargeIdMarker,
} from "./internal/approvalReturn";
import { consumeApprovalCompletionAnalytics } from "./internal/approvalReturnStorage";
import {
  type EntityActionDialogInput,
  projectEntityActionDialog,
} from "./internal/dialogProjection";
import {
  projectBillingPage,
  projectPlanChangeDialog,
} from "./internal/pageProjection";
import {
  createInitialSelectionFromDesiredPlan,
  desiredPlanCadence,
  transitionSelection,
} from "./internal/selection";
import type { BillingCatalog, BillingSnapshot } from "./internal/types";
import { useBillingCommandExecutor } from "./internal/useBillingCommandExecutor";
import { useBillingWorkflowSnapshot } from "./internal/useBillingWorkflowSnapshot";
import {
  initialBillingWorkflowState,
  selectWorkflowSnapshot,
  workflowReducer,
} from "./internal/workflowReducer";

const nowSeconds = (): number => Math.floor(Date.now() / 1_000);

// Terminal failures that a toast can't resolve: the merchant needs a recovery
// route, so they escalate to the command-blocked dialog instead.
const ESCALATED_ERROR_CODES: ReadonlySet<string> = new Set([
  "shopify_app_subscription_not_active",
]);

const emitAnalytics = (event: BillingCommandAnalyticsEvent): void => {
  switch (event.type) {
    case "billing-plan-change-completed":
      mpBillingPlanChangeCompleted({
        billingCycle: event.billingCycle,
        changeType: event.changeType,
        modules: [...event.moduleIds],
        modulesCount: event.moduleIds.length,
        planName: event.planId ?? MODULAR_PLAN_NAME,
        totalAmount: event.totalAmount,
      });
      return;
    case "billing-shopify-approval-started":
      mpBillingShopifyApprovalStarted({
        source: "plan_change",
        billingCycle: event.billingCycle,
        changeType: event.changeType,
        modules: event.moduleIds,
        planName: event.planId ?? MODULAR_PLAN_NAME,
      });
      return;
    case "billing-plan-downgrade-started":
      mpBillingPlanDowngradeStarted({
        activeModules: [...event.activeModuleIds],
        ...(event.currentPlanId ? { currentPlan: event.currentPlanId } : {}),
      });
      return;
    case "billing-module-removed":
      mpBillingModuleRemoved({
        moduleName: event.moduleName,
        remainingModules: [...event.remainingModuleIds],
        remainingModulesCount: event.remainingModuleIds.length,
      });
      return;
    case "billing-plan-cancel-confirmed":
      mpBillingPlanCancelConfirmed({
        activeModules: [...event.activeModuleIds],
        currentPlan: event.currentPlanId,
      });
      return;
    case "billing-checkout-error-shown":
      mpBillingCheckoutErrorShown({
        errorMessage: event.errorMessage,
        errorType: event.errorType,
        step: event.step,
      });
      return;
    default: {
      // A planned event with no mapping would vanish silently, so a new
      // variant has to fail the build here instead.
      const unmapped: never = event;
      return unmapped;
    }
  }
};

const trialModuleIds = (
  snapshot: BillingSnapshot,
  catalog: BillingCatalog
): string[] => {
  const trialPriceIds = new Set(
    snapshot.subscriptionAccessData.subscriptions.historical
      .filter(({ status }) => status === "trialing")
      .flatMap(({ priceIds }) => priceIds)
  );

  return catalog.modules
    .filter((module) => {
      const keys = new Set([module.id, ...(module.variantProductKeys ?? [])]);
      return snapshot.subscriptionAccessData.access.products.some(
        ({ entitlementProfileKey, priceIds }) =>
          entitlementProfileKey !== null &&
          keys.has(entitlementProfileKey) &&
          priceIds.some((priceId) => trialPriceIds.has(priceId))
      );
    })
    .map(({ id }) => id);
};

const toPublicActionDialog = (
  dialog: ReturnType<typeof projectEntityActionDialog>
): BillingWorkflowDialogViewModel => {
  if (!dialog) return { type: "closed" };
  const { kind, ...model } = dialog;
  return { ...model, type: kind } as BillingWorkflowDialogViewModel;
};

export const useBillingSettingsWorkflow = (): BillingSettingsWorkflow => {
  const [state, dispatch] = useReducer(
    workflowReducer,
    initialBillingWorkflowState
  );
  const source = useBillingWorkflowSnapshot({
    enabled: true,
    pollForApprovalReturn: state.approvalReturn.status === "waiting",
  });
  const websocket = useWebsocket();
  const portal = useGetBillingPortal(false);
  const appDispatch = useDispatch();
  const [searchParams] = useSearchParams();
  const { hash, pathname, search } = useLocation();
  const navigate = useNavigate();
  const viewedAccountStateRef = useRef<string | null>(null);
  const reducerOrgIdRef = useRef(source.orgId);
  // The plan that knew which completion events to fire died with the navigation
  // to Shopify, so the return path carries them across in its stash instead.
  const completionAnalyticsRef = useRef<
    readonly BillingCommandAnalyticsEvent[]
  >([]);

  const feedback = useCallback(
    (value: BillingCommandFeedback) => {
      if (value.tone === "error") toast.error(value.message);
      else if (value.tone === "success") toast.success(value.message);
      else toast.info(value.message);
      if (value.confetti) appDispatch(setConfetti(true));
    },
    [appDispatch]
  );

  const redirect = useCallback((url: string) => {
    window.location.assign(url);
  }, []);

  const onTerminalFailure = (errorCode: string): boolean => {
    if (!ESCALATED_ERROR_CODES.has(errorCode)) return false;
    dispatch({
      type: "show-command-blocked",
      title: "Your Shopify subscription is no longer active",
      body: "This plan can't be resumed because its Shopify subscription was already cancelled. Start a new plan to continue using your modules.",
      acknowledgeLabel: "Got it",
      recovery: "start-new-plan",
    });
    return true;
  };

  const executor = useBillingCommandExecutor({
    command: state.command,
    dispatch,
    emitAnalytics,
    emitFeedback: feedback,
    invalidateSubscriptionAccess: source.invalidateSubscriptionAccess,
    isRawSnapshotFetching: source.isRawSnapshotFetching,
    onTerminalFailure,
    orgId: source.orgId,
    rawSnapshot: source.rawSnapshot,
    redirect,
    websocketMessage: websocket.lastJsonMessage,
  });

  const projectedSnapshot = useMemo(
    () =>
      source.rawSnapshot
        ? selectWorkflowSnapshot(source.rawSnapshot, state, source.orgId)
        : null,
    [source.rawSnapshot, state]
  );

  const pageCatalog = useMemo(() => {
    if (!projectedSnapshot || !source.catalog) return null;

    const catalogs = {
      month: buildBillingCatalog(projectedSnapshot, "month"),
      year: buildBillingCatalog(projectedSnapshot, "year"),
    };
    const cadence =
      desiredPlanCadence(
        projectedSnapshot.subscriptionAccessData.planIntent?.desiredPlan,
        catalogs,
        projectedSnapshot.livePrices
      ) ?? source.catalog.cadence;

    return catalogs[cadence];
  }, [projectedSnapshot, source.catalog]);

  const handlePlan = useCallback(
    (plan: BillingCommandPlan): boolean => {
      if (plan.kind === "planned") {
        void executor.execute(plan);
        return true;
      }
      if (plan.kind === "no-op") {
        feedback(plan.feedback);
        return false;
      }
      for (const event of plan.analytics) emitAnalytics(event);
      if (plan.feedback.tone === "dialog") {
        dispatch({
          type: "show-command-blocked",
          acknowledgeLabel: plan.feedback.acknowledgeLabel,
          body: plan.feedback.body,
          title: plan.feedback.title,
        });
      } else {
        feedback(plan.feedback);
      }
      return false;
    },
    [executor, feedback]
  );

  const runIntent = useCallback(
    (intent: Parameters<typeof planBillingCommand>[0]["intent"]): boolean => {
      if (!source.rawSnapshot) return false;
      return handlePlan(
        planBillingCommand({
          intent,
          nowSec: nowSeconds(),
          snapshot: source.rawSnapshot,
          stagedCouponId: state.stagedCoupon?.id ?? null,
        })
      );
    },
    [handlePlan, source.rawSnapshot, state.stagedCoupon]
  );

  const openPlanChange = useCallback(() => {
    if (!pageCatalog || !projectedSnapshot) return;
    dispatch({
      type: "open-plan-change",
      selection: createInitialSelectionFromDesiredPlan(
        pageCatalog,
        projectedSnapshot.subscriptionAccessData.planIntent?.desiredPlan,
        {},
        projectedSnapshot.livePrices
      ),
    });
  }, [pageCatalog, projectedSnapshot]);

  const preselectPlanChange = useCallback(
    (entityIds: readonly string[]) => {
      if (!pageCatalog || !projectedSnapshot) return;
      const entities = entityIds
        .map((id) => entityFor(pageCatalog, id))
        .filter((entity): entity is NonNullable<typeof entity> =>
          Boolean(entity)
        );
      if (entities.length === 0) return;

      const planEntity = entities.find((entity) => entity.type === "plan");
      const moduleIds = entities
        .filter((entity) => entity.type === "module")
        .map((entity) => entity.id);

      const selection = createInitialSelectionFromDesiredPlan(
        pageCatalog,
        projectedSnapshot.subscriptionAccessData.planIntent?.desiredPlan,
        {
          ...(planEntity ? { preselectedPlanId: planEntity.id } : {}),
          ...(moduleIds.length > 0 ? { preselectedModuleIds: moduleIds } : {}),
        },
        projectedSnapshot.livePrices
      );
      // Smart Pricing adds open the kickoff before module selection, carrying
      // the preselection into the billing dialog's next step.
      const routeThroughKickoff = entities.some((entity) =>
        SMART_PRICING_VARIANTS.has(entity.id)
      );
      dispatch({
        type: routeThroughKickoff
          ? "preselect-smart-pricing-kickoff"
          : "preselect-plan-change",
        selection,
      });
    },
    [pageCatalog, projectedSnapshot]
  );

  const updateSelection = useCallback(
    (intent: BillingSelectionIntent) => {
      if (state.dialog.type !== "plan-change" || !projectedSnapshot) return;
      const cadence =
        intent.type === "change_cadence"
          ? intent.cadence === "annual"
            ? "year"
            : "month"
          : state.dialog.selection.cadence;
      const catalog = buildBillingCatalog(projectedSnapshot, cadence);
      dispatch({
        type: "update-selection",
        selection: transitionSelection(state.dialog.selection, intent, catalog),
      });
    },
    [projectedSnapshot, state.dialog]
  );

  const submit = useCallback(() => {
    if (state.dialog.type !== "plan-change") return false;

    return runIntent({
      type: "submit-selection",
      selection: state.dialog.selection,
    });
  }, [runIntent, state.dialog]);

  const applyCoupon = useCallback(
    (
      entityId: string,
      couponId: string,
      couponLabel: string,
      percentOff?: number,
      amountOff?: number
    ) =>
      runIntent({
        type: "apply-coupon",
        entityId,
        couponId,
        couponLabel,
        percentOff,
        amountOff,
      }),
    [runIntent]
  );

  const stageCoupon = useCallback(
    (
      coupon: Readonly<{
        id: string;
        percentOff?: number;
        amountOff?: number;
      }> | null
    ) => {
      dispatch({ type: "stage-coupon", coupon });
    },
    []
  );

  const openSmartPricingSelection = useCallback(() => {
    if (state.dialog.type !== "smart-pricing-kickoff") return;
    dispatch({
      type: "preselect-plan-change",
      selection: state.dialog.selection,
    });
  }, [state.dialog]);

  const requestEntityAction = useCallback(
    (intent: BillingEntityActionIntent) => {
      if (!source.rawSnapshot || !pageCatalog) return;
      if (
        intent.type === "release" ||
        intent.type === "cancel-pending-removal"
      ) {
        runIntent(intent);
        return;
      }

      if (intent.type === "pause" && intent.timing === "immediate") {
        runIntent({
          type: "pause",
          entityId: intent.entityId,
          expectedPauseAt: null,
        });
        return;
      }

      const entity = entityFor(pageCatalog, intent.entityId);
      if (!entity) return;
      if (intent.type === "remove-module" || intent.type === "cancel-trial") {
        dispatch({ type: "request-entity-action", action: intent });
        return;
      }

      // The core module can't be cancelled on its own, so the command this
      // flow eventually runs targets the plan, not the module.
      if (intent.type === "cancel-core-module") {
        dispatch({
          type: "open-core-module-cancel",
          entityId: intent.entityId,
          expectedCancellationAt: subscriptionPeriodEndFor(
            source.rawSnapshot,
            pageCatalog,
            intent.entityId,
            nowSeconds()
          ),
          planEntityId: pageCatalog.currentPlan?.id ?? intent.entityId,
        });
        return;
      }

      if (intent.type === "cancel") {
        const model = projectEntityActionDialog({
          commitmentStartAt: entity.startDate,
          entity,
          kind: "cancel",
          timing: intent.timing,
        });
        dispatch({
          type: "request-entity-action",
          action: {
            type: "cancel",
            entityId: entity.id,
            expectedCancellationAt:
              model?.kind === "cancel" && model.effectiveAt !== null
                ? model.effectiveAt
                : subscriptionPeriodEndFor(
                    source.rawSnapshot,
                    pageCatalog,
                    entity.id,
                    nowSeconds()
                  ),
            timing: intent.timing,
          },
        });
        return;
      }

      const model = projectEntityActionDialog({
        commitmentStartAt: entity.startDate,
        entity,
        kind: "pause",
      });

      dispatch({
        type: "request-entity-action",
        action: {
          type: "pause",
          entityId: entity.id,
          expectedPauseAt: model?.kind === "pause" ? model.effectiveAt : null,
        },
      });
    },
    [pageCatalog, runIntent, source.rawSnapshot]
  );

  const confirmDialog = useCallback(
    (intent: BillingDialogConfirmationIntent) => {
      if (
        state.dialog.type !== intent.type ||
        state.dialog.entityId !== intent.entityId
      ) {
        return;
      }
      switch (state.dialog.type) {
        case "cancel": {
          const entity = pageCatalog
            ? entityFor(pageCatalog, state.dialog.entityId)
            : undefined;
          const model = projectEntityActionDialog({
            commitmentStartAt: entity?.startDate,
            entity,
            kind: "cancel",
            timing: state.dialog.timing,
          });
          if (model?.kind !== "cancel" || model.disabledReason) return;
          runIntent({
            type: "cancel",
            entityId: state.dialog.entityId,
            expectedCancellationAt: state.dialog.expectedCancellationAt,
            timing: state.dialog.timing,
          });
          return;
        }
        case "pause": {
          const entity = pageCatalog
            ? entityFor(pageCatalog, state.dialog.entityId)
            : undefined;
          const model = projectEntityActionDialog({
            commitmentStartAt: entity?.startDate,
            entity,
            kind: "pause",
          });
          if (model?.kind !== "pause" || model.disabledReason) return;
          runIntent({
            type: "pause",
            entityId: state.dialog.entityId,
            expectedPauseAt: state.dialog.expectedPauseAt,
          });
          return;
        }
        case "cancel-core-module": {
          if (state.dialog.step !== "confirm") return;
          runIntent({
            type: "cancel",
            entityId: state.dialog.planEntityId,
            expectedCancellationAt: state.dialog.expectedCancellationAt,
            timing: "immediate",
          });
          return;
        }
        case "remove-module":
          runIntent({
            type: "remove-module",
            entityId: state.dialog.entityId,
          });
          return;
        case "cancel-trial":
          runIntent({
            type: "cancel-trial",
            entityId: state.dialog.entityId,
          });
      }
    },
    [pageCatalog, runIntent, state.dialog]
  );

  const upgradeTrial = useCallback(() => {
    if (!pageCatalog || !projectedSnapshot) return;
    dispatch({
      type: "open-plan-change",
      selection: createInitialSelectionFromDesiredPlan(
        pageCatalog,
        projectedSnapshot.subscriptionAccessData.planIntent?.desiredPlan,
        {
          preselectedModuleIds: trialModuleIds(projectedSnapshot, pageCatalog),
        },
        projectedSnapshot.livePrices
      ),
    });
  }, [pageCatalog, projectedSnapshot]);

  const manage = useCallback(() => {
    if (source.management.type === "shopify") {
      redirect(source.management.url);
      return;
    }
    if (source.management.type !== "stripe" || !source.stripeSubscriptionId) {
      return;
    }
    portal.mutate(
      {
        body: { stripeSubscriptionId: source.stripeSubscriptionId },
        pathParams: undefined,
        queryParams: undefined,
      },
      {
        onError: () => {
          mpBillingCheckoutErrorShown({
            errorMessage: "Failed to retrieve billing portal URL",
            errorType: "portal_redirect_failed",
            step: "confirmation",
          });
          toast.error("Failed to open the billing portal. Please try again.");
        },
      }
    );
  }, [portal, redirect, source.management, source.stripeSubscriptionId]);

  const page = useMemo(
    () =>
      projectedSnapshot && pageCatalog && source.status === "ready"
        ? projectBillingPage({
            catalog: pageCatalog,
            management: source.management,
            ordersPerMonth: source.ordersPerMonth,
            snapshot: projectedSnapshot,
            stagedCoupon: state.stagedCoupon,
          })
        : null,
    [
      pageCatalog,
      projectedSnapshot,
      source.management,
      source.ordersPerMonth,
      source.status,
      state.stagedCoupon,
    ]
  );

  const command = useMemo<BillingCommandViewModel>(() => {
    const { command: stateCommand } = state;
    const { status } = stateCommand;

    if (status === "idle") {
      return { awaitsProviderApproval: false, status };
    }

    const { plan } = stateCommand;
    const { mayAwaitShopifyApproval } = plan;

    return {
      awaitsProviderApproval: mayAwaitShopifyApproval,
      status,
    };
  }, [state.command]);

  const dialog = useMemo<BillingWorkflowDialogViewModel>(() => {
    if (state.dialog.type === "closed") return { type: "closed" };
    if (state.dialog.type === "command-blocked") return state.dialog;
    if (state.dialog.type === "smart-pricing-kickoff") {
      return { type: "smart-pricing-kickoff" };
    }
    if (state.dialog.type === "smart-pricing-whats-next") {
      return {
        type: "smart-pricing-whats-next",
        orderVolumeTier: state.dialog.orderVolumeTier,
        tierPurchased: state.dialog.tierPurchased,
      };
    }
    if (!projectedSnapshot || !pageCatalog) return { type: "closed" };
    if (state.dialog.type === "plan-change") {
      const catalog = buildBillingCatalog(
        projectedSnapshot,
        state.dialog.selection.cadence
      );
      return projectPlanChangeDialog({
        catalog,
        nowSec: nowSeconds(),
        ordersPerMonth: source.ordersPerMonth,
        selection: state.dialog.selection,
        snapshot: projectedSnapshot,
        stagedCoupon: state.stagedCoupon,
      });
    }

    let input: EntityActionDialogInput;
    if (state.dialog.type === "remove-module") {
      input = {
        catalog: pageCatalog,
        kind: "remove-module",
        moduleId: state.dialog.entityId,
        nowSec: nowSeconds(),
        snapshot: projectedSnapshot,
      };
    } else if (state.dialog.type === "cancel-trial") {
      input = {
        catalog: pageCatalog,
        kind: "cancel-trial",
        moduleId: state.dialog.entityId,
      };
    } else if (state.dialog.type === "cancel-core-module") {
      input = {
        catalog: pageCatalog,
        kind: "cancel-core-module",
        moduleId: state.dialog.entityId,
        nowSec: nowSeconds(),
        snapshot: projectedSnapshot,
        step: state.dialog.step,
      };
    } else {
      const entity = entityFor(pageCatalog, state.dialog.entityId);
      input =
        state.dialog.type === "cancel"
          ? {
              commitmentStartAt: entity?.startDate,
              entity,
              kind: "cancel",
              timing: state.dialog.timing,
            }
          : {
              commitmentStartAt: entity?.startDate,
              entity,
              kind: "pause",
            };
    }
    return toPublicActionDialog(projectEntityActionDialog(input));
  }, [
    pageCatalog,
    projectedSnapshot,
    source.ordersPerMonth,
    state.dialog,
    state.stagedCoupon,
  ]);

  useEffect(() => {
    if (reducerOrgIdRef.current === source.orgId) return;
    reducerOrgIdRef.current = source.orgId;
    dispatch({ type: "organization-changed" });
  }, [source.orgId]);

  useEffect(() => {
    if (!page || viewedAccountStateRef.current === page.accountState) return;
    viewedAccountStateRef.current = page.accountState;
    mpBillingPageViewed(page.accountState);
  }, [page]);

  const selectedParam = searchParams.get("selected");
  useEffect(() => {
    if (source.status !== "ready" || !searchParams.has("selected")) return;
    const ids = selectedParam?.split(",").filter(Boolean) ?? [];
    if (ids.length > 0) preselectPlanChange(ids);
    const next = new URLSearchParams(searchParams);
    next.delete("selected");
    const search = next.toString();
    navigate(
      { hash, pathname, search: search ? `?${search}` : "" },
      { replace: true }
    );
  }, [
    hash,
    navigate,
    pathname,
    preselectPlanChange,
    searchParams,
    selectedParam,
    source.status,
  ]);

  // Arrival from a Blocking Approval. The marker is consumed here — both the
  // `charge_id` in the URL and the stashed completion events — so a mid-wait
  // refresh lands on a plain billing page instead of a re-armed one.
  useEffect(() => {
    if (source.status !== "ready" || !source.rawSnapshot) return;
    const marker = readChargeIdMarker({ hash, search });
    if (!marker) return;

    const arrival = readApprovalReturnArrival(source.rawSnapshot);
    if (arrival.kind === "ignored") return;

    mpBillingShopifyApprovalReturned({
      alreadyApplied: arrival.alreadyApplied,
      approvalKind: arrival.approvalKind,
    });
    completionAnalyticsRef.current = consumeApprovalCompletionAnalytics(
      source.orgId
    );
    dispatch({
      type: "approval-return-armed",
      approvalKind: arrival.approvalKind,
      armedAt: Date.now(),
    });
    navigate(
      { hash: marker.hash, pathname, search: marker.search },
      { replace: true }
    );
  }, [
    hash,
    navigate,
    pathname,
    search,
    source.orgId,
    source.rawSnapshot,
    source.status,
  ]);

  // The celebration's truth is `planIntent.state === "applied"`, never Shopify's
  // `approved` and never a completed command. Settling is what makes it fire
  // once: a later refetch that re-reports `applied` finds the wait already over.
  useEffect(() => {
    if (state.approvalReturn.status !== "waiting" || !source.rawSnapshot)
      return;
    const outcome = readApprovalReturnOutcome(source.rawSnapshot);
    if (outcome === null) return;

    dispatch({ type: "approval-return-settled", outcome });
    if (outcome === "activated") {
      for (const event of completionAnalyticsRef.current) emitAnalytics(event);
      completionAnalyticsRef.current = [];
    }
    const message = APPROVAL_RETURN_FEEDBACK[outcome];
    if (message) feedback(message);
  }, [feedback, source.rawSnapshot, state.approvalReturn]);

  useEffect(() => {
    const { approvalReturn } = state;
    if (approvalReturn.status !== "waiting") return;
    const timer = window.setTimeout(
      () => dispatch({ type: "approval-return-settled", outcome: "timed_out" }),
      Math.max(
        0,
        approvalReturn.armedAt + APPROVAL_RETURN_POLL_CAP_MS - Date.now()
      )
    );
    return () => window.clearTimeout(timer);
  }, [state.approvalReturn]);

  return {
    actions: {
      acknowledgeBlocked: () => dispatch({ type: "acknowledge-blocked" }),
      advanceCoreModuleCancel: () =>
        dispatch({ type: "advance-core-module-cancel" }),
      applyCoupon,
      cancelSmartPricingKickoff: () =>
        dispatch({ type: "cancel-smart-pricing-kickoff" }),
      confirmDialog,
      openSmartPricingSelection,
      dismissDialog: () => dispatch({ type: "dismiss-dialog" }),
      manage,
      openPlanChange,
      openSmartPricingKickoff: () =>
        dispatch({ type: "open-smart-pricing-kickoff" }),
      preselectPlanChange,
      requestEntityAction,
      retry: source.refetch,
      showSmartPricingWhatsNext: (tierPurchased) =>
        dispatch({
          type: "show-smart-pricing-whats-next",
          orderVolumeTier: orderVolumeTier(source.ordersPerMonth),
          tierPurchased,
        }),
      stageCoupon,
      submit,
      updateSelection,
      upgradeTrial,
    },
    awaitsApprovalReturn: state.approvalReturn.status === "waiting",
    command,
    dialog,
    failureReason: source.failureReason,
    ordersPerMonth: source.ordersPerMonth,
    page,
    stagedCoupon: state.stagedCoupon,
    status: source.status === "error" ? "error" : page ? "ready" : "loading",
  };
};
